import { useMemo, useRef } from "react";
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";
import "survey-core/survey-core.min.css";
import "@/features/tutor/components/ExitTicketSurvey.css";
import "@/features/tutor/utils/surveyCustomProperties";
import {
  choiceImage,
  choiceImageUrl,
  choiceLabel,
} from "@/features/tutor/utils/surveyChoiceImage";
import { renderMath } from "@/utils/mathjax";
import {
  makeImagesZoomable,
  parseLatex,
  serializeToDisplay,
} from "@ever-tutor/et-ui";

type LatexDoc = ReturnType<typeof parseLatex>;
type LatexNode = LatexDoc["nodes"][number];

const isSpaceNode = (node: LatexNode): boolean => {
  const n = node as { type?: string; value?: string };
  return n.type === "text" && n.value === " ";
};

const renderLatexBlock = (element: HTMLElement, latex: string): void => {
  renderMath(element, serializeToDisplay(parseLatex(latex)), {
    displayFractions: true,
  });
};

const renderLatexWrapped = (element: HTMLElement, latex: string): void => {
  const doc = parseLatex(latex);
  const segments: LatexNode[][] = [];
  let current: LatexNode[] = [];

  for (const node of doc.nodes) {
    if (isSpaceNode(node)) {
      segments.push(current);
      current = [];
    } else {
      current.push(node);
    }
  }
  segments.push(current);

  if (segments.length <= 1) {
    renderLatexBlock(element, latex);
    return;
  }

  element.innerHTML = "";
  segments.forEach((segment, i) => {
    if (segment.length > 0) {
      const wordSpan = document.createElement("span");
      wordSpan.className = "et-word-group";
      renderLatexBlock(wordSpan, serializeToDisplay({ nodes: segment }));
      element.appendChild(wordSpan);
    }
    if (i < segments.length - 1) {
      const spaceSpan = document.createElement("span");
      spaceSpan.className = "et-editing-space";
      spaceSpan.textContent = " ";
      element.appendChild(spaceSpan);
    }
  });
};

const overlayOnto = (
  original: HTMLElement,
  latex: string,
  className: string,
): HTMLElement | null => {
  if (original.previousElementSibling?.classList.contains(className))
    return null;
  const parent = original.parentElement;
  if (!parent) return null;
  const overlay = document.createElement("div");
  overlay.className = `et-katex-display ${className}`;
  parent.insertBefore(overlay, original);
  if (latex) {
    try {
      renderLatexWrapped(overlay, latex);
    } catch {
      overlay.remove();
      return null;
    }
  }
  original.style.display = "none";
  return overlay;
};

interface AssessmentPreviewSurveyProps {
  surveyJson: Record<string, unknown>;
  singlePage?: boolean;
  onZoomImage?: (src: string, alt: string) => void;
}

const AssessmentPreviewSurvey = ({
  surveyJson,
  singlePage = false,
  onZoomImage,
}: AssessmentPreviewSurveyProps) => {
  const onZoomImageRef = useRef(onZoomImage);
  onZoomImageRef.current = onZoomImage;

  const model = useMemo(() => {
    const m = new Model(surveyJson);
    m.mode = "display";
    m.showNavigationButtons = false;
    m.showCompletedPage = false;
    m.showProgressBar = "off";
    if (singlePage) m.questionsOnPageMode = "singlePage";

    m.onAfterRenderQuestion.add((_sender, options) => {
      const el = options.htmlElement;
      const question = options.question;

      const zoom = onZoomImageRef.current;
      if (zoom) makeImagesZoomable(el, zoom);

      const latexTitle = (question as { question?: string }).question;
      const titleEl = el.querySelector(
        ".sd-question__title",
      ) as HTMLElement | null;
      if (titleEl && latexTitle) {
        overlayOnto(titleEl, latexTitle, "et-katex-display--title");
      }

      const questionType = question.getType();
      if (questionType === "radiogroup" || questionType === "checkbox") {
        const q = question as {
          visibleChoices?: unknown[];
          choices?: unknown[];
        };
        const choices = q.visibleChoices || q.choices || [];
        let painted = false;
        el.querySelectorAll(".sd-item__control-label").forEach(
          (labelEl, idx) => {
            const text = choiceLabel(choices[idx]);
            const hasImage = Boolean(choiceImageUrl(choices[idx]));
            if (!text && !hasImage) return;
            const overlay = overlayOnto(
              labelEl as HTMLElement,
              text,
              "et-katex-display--radio-option",
            );
            if (!overlay || !hasImage) return;
            const image = choiceImage(choices[idx], idx);
            if (image) overlay.prepend(image);
            painted = true;
          },
        );
        if (painted && zoom) makeImagesZoomable(el, zoom);
      }
    });

    return m;
  }, [surveyJson, singlePage]);

  return <Survey model={model} />;
};

export default AssessmentPreviewSurvey;
